import { pickKeys } from '@repo/utils/object';
import { nullishToUndefinedable } from '@repo/utils/valibot';
import * as v from 'valibot';
import { StructuredContentDocumentSchema } from '../../cross-site/structured-content/schema';
import { BaseOfferSchema } from './base';
import type { BaseOffer } from './types';

export const isMonetizing = (monetizable: BaseOffer['monetizable']) => {
  return monetizable.includes('yes');
};

export const transformOfferDetailsFromMarketplaceEntity = (entity: BaseOffer, slug?: string) => {
  const baseEntityProps = [
    'applyLink',
    'approvalLikelihood',
    'legacyId',
    'annualFeeDetails',
    'monetizable',
    'marketingBullets',
    'reviewLink',
    'starRating',
    'versionId',
    'eligibleForNerdWalletPlusBonus',
  ] as const;
  const isMonetizable = isMonetizing(entity.monetizable);

  const isActive = entity.status.toLowerCase() === 'active';

  const baseEntity = pickKeys(entity, baseEntityProps);
  const details = {
    ...baseEntity,
    annualFee: {
      value: entity.annualFee,
      driverValue: entity.driverAnnualFee?.value,
    },
    applyPhoneNumber: entity.phoneNumber,
    isMonetizing: isMonetizable,
    isActive,
    isReview: !isActive || !isMonetizable || !entity.applyLink,
    cons: entity.consBullets,
    pros: entity.prosBullets,
    termsFeesLink: entity.termsAndFeesLink,
    rewardsRates: entity.rewardsRates,
    apr: {
      min: entity.aprMin,
      max: entity.aprMax,
      ongoingRate: entity.ongoingApr,
      introMessageOptions: {
        short: entity.driverIntroApr?.value,
      },
      introPurchases: entity.introPurchases,
      introBalanceTransfers: entity.introBalanceTransfers,
    },
    creditScore: {
      min: entity.minFico,
      max: entity.maxFico,
      limitedCreditHistory: entity.limitedCreditHistory,
    },
    rewardRateDetails: {
      description: entity.rewardRateNotes,
      rateString: entity.driverRewardRate?.value,
      rewardTypeDescription: entity.driverRewardRate?.description,
      type: entity.rewardType?.toLowerCase(),
      tooltipString:
        entity.driverRewardRate?.valueTooltip !== 'N/A'
          ? entity.driverRewardRate?.valueTooltip
          : undefined,
    },
    signUpBonusDetails: {
      bonus: entity.signUpBonus,
      description: entity.signUpBonusDetails,
      type: entity.signUpBonusType?.toLowerCase(),
      signUpBonusDetailsAnnotation: entity.signUpBonusDetailsAnnotation,
    },
    introOfferDetails: {
      offerString: entity.driverIntroOffer.value,
      tooltipString:
        entity.driverIntroOffer.valueTooltip !== 'N/A'
          ? entity.driverIntroOffer.valueTooltip
          : undefined,
      type: entity.signUpBonusType?.toLowerCase(),
    },
    balanceTransferFee: entity.driverBalanceTransferFeeDescription?.value,
    balanceTransferIntroTime: entity.balanceTransferIntroTime,
    billingCycleUnits: entity.billingCycleUnits,
    minimumDeposit: entity.driverMinimumDeposit?.value,
  };

  return details;
};

const offerDetailsBaseFields = v.pick(BaseOfferSchema, [
  'approvalLikelihood',
  'legacyId',
  'annualFeeDetails',
  'monetizable',
  'marketingBullets',
  'reviewLink',
  'starRating',
  'versionId',
  'eligibleForNerdWalletPlusBonus',
]);

export const TransformedOfferDetailsSchema = v.object({
  ...offerDetailsBaseFields.entries,
  applyLink: BaseOfferSchema.entries.applyLink,
  annualFee: v.object({
    value: BaseOfferSchema.entries.annualFee,
    driverValue: nullishToUndefinedable(v.string()),
  }),
  applyPhoneNumber: nullishToUndefinedable(BaseOfferSchema.entries.phoneNumber),
  isMonetizing: v.boolean(),
  isActive: v.boolean(),
  isReview: v.boolean(),
  cons: v.union([v.array(v.string()), v.array(StructuredContentDocumentSchema)]),
  pros: v.union([v.array(v.string()), v.array(StructuredContentDocumentSchema)]),
  termsFeesLink: nullishToUndefinedable(BaseOfferSchema.entries.termsAndFeesLink),
  rewardsRates: BaseOfferSchema.entries.rewardsRates,
  apr: v.object({
    min: BaseOfferSchema.entries.aprMin,
    max: BaseOfferSchema.entries.aprMax,
    ongoingRate: nullishToUndefinedable(BaseOfferSchema.entries.ongoingApr),
    introMessageOptions: v.object({
      short: nullishToUndefinedable(v.string()),
    }),
    introPurchases: nullishToUndefinedable(BaseOfferSchema.entries.introPurchases),
    introBalanceTransfers: nullishToUndefinedable(BaseOfferSchema.entries.introBalanceTransfers),
  }),
  creditScore: v.object({
    min: BaseOfferSchema.entries.minFico,
    max: BaseOfferSchema.entries.maxFico,
    limitedCreditHistory: BaseOfferSchema.entries.limitedCreditHistory,
  }),
  rewardRateDetails: v.object({
    description: nullishToUndefinedable(BaseOfferSchema.entries.rewardRateNotes),
    rateString: nullishToUndefinedable(v.string()),
    rewardTypeDescription: nullishToUndefinedable(v.string()),
    type: nullishToUndefinedable(v.string()),
    tooltipString: nullishToUndefinedable(v.string()),
  }),
  signUpBonusDetails: v.object({
    bonus: BaseOfferSchema.entries.signUpBonus,
    description: BaseOfferSchema.entries.signUpBonusDetails,
    type: nullishToUndefinedable(v.string()),
    signUpBonusDetailsAnnotation: nullishToUndefinedable(
      BaseOfferSchema.entries.signUpBonusDetailsAnnotation,
    ),
  }),
  introOfferDetails: v.object({
    offerString: nullishToUndefinedable(v.string()),
    tooltipString: nullishToUndefinedable(v.string()),
    type: nullishToUndefinedable(v.string()),
  }),
  balanceTransferFee: nullishToUndefinedable(v.string()),
  balanceTransferIntroTime: BaseOfferSchema.entries.balanceTransferIntroTime,
  billingCycleUnits: BaseOfferSchema.entries.billingCycleUnits,
  minimumDeposit: nullishToUndefinedable(v.string()),
});

export const OfferDetailsSchema = v.pipe(
  BaseOfferSchema,
  v.transform(val => transformOfferDetailsFromMarketplaceEntity(val)),
);
export type OfferDetails = v.InferOutput<typeof OfferDetailsSchema>;
